import {
  WBBox,
  WBConfirmationDialogue,
  WBFlex,
  WBIcon,
  WBIconButton,
  WBTypography,
} from '@admiin-com/ds-web';
import {
  LinearProgress,
  linearProgressClasses,
  LinearProgressProps,
  styled,
  useTheme,
} from '@mui/material';
import { useState } from 'react';

interface Props {
  name: string;
  id: string;
  size: string;
  progress: number;
  onClose: () => void;
  removeConfirmationTitle?: string;
  removeConfirmationDescription?: string;
  noBorderTop?: boolean;
  type?: 'UPLOAD' | 'EMAIL';
}
export const FileUpload = ({
  removeConfirmationTitle,
  removeConfirmationDescription,
  name,
  size,
  progress,
  noBorderTop,
  onClose,
  type,
}: Props) => {
  const [open, setOpen] = useState(false);
  const FileIcon =
    useTheme().palette.mode === 'dark' ? FileWhiteIcon : FileBlackIcon;

  return (
    <Container
      sx={{ borderTopWidth: noBorderTop ? 0 : 2 }}
      data-testid={
        progress === 100
          ? 'upload-success'
          : progress > 0
          ? 'upload-progress'
          : 'upload-item'
      }
    >
      <WBFlex justifyContent={'space-between'}>
        <WBFlex alignItems={'center'} gap={1}>
          {type === 'EMAIL' ? <EmailIcon /> : <FileIcon />}
          <WBBox>
            <WBTypography>{name}</WBTypography>
            <WBTypography color="text.secondary" variant="body2">
              {size}
            </WBTypography>
          </WBBox>
        </WBFlex>
        <WBIconButton
          onClick={() => {
            if (!removeConfirmationTitle) onClose();
            else {
              if (progress === 0) setOpen(true);
              else {
                onClose();
              }
            }
          }}
        >
          <WBIcon name="Close" size={'medium'} color="grey" />
        </WBIconButton>
      </WBFlex>
      {progress > 0 && (
        <LinearProgressWithLabel value={progress} variant="determinate" />
      )}
      {removeConfirmationTitle && (
        <WBConfirmationDialogue
          open={open}
          onClose={() => setOpen(false)}
          title={removeConfirmationTitle}
          onOK={() => {
            setOpen(false);
            onClose();
          }}
        >
          {removeConfirmationDescription}
        </WBConfirmationDialogue>
      )}
    </Container>
  );
};
function LinearProgressWithLabel(
  props: LinearProgressProps & { value: number }
) {
  return (
    <WBBox sx={{ display: 'flex', alignItems: 'center' }}>
      <WBBox sx={{ width: '100%', mr: 1 }}>
        <BorderLinearProgress variant="determinate" {...props} />
      </WBBox>
      <WBBox sx={{ minWidth: 35 }}>
        <WBTypography
          variant="body2"
          sx={{ color: 'text.secondary' }}
        >{`${Math.round(props.value)}%`}</WBTypography>
      </WBBox>
    </WBBox>
  );
}

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: theme.palette.primary.main,
  },
}));
const Container = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(1),
  border: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1),
}));

export const FileBlackIcon = () => {
  return (
    <svg
      width="27"
      height="27"
      viewBox="0 0 27 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.625 10.125V3.9375L20.8125 10.125M6.75 2.25C5.50125 2.25 4.5 3.25125 4.5 4.5V22.5C4.5 23.0967 4.73705 23.669 5.15901 24.091C5.58097 24.5129 6.15326 24.75 6.75 24.75H20.25C20.8467 24.75 21.419 24.5129 21.841 24.091C22.2629 23.669 22.5 23.0967 22.5 22.5V9L15.75 2.25H6.75Z"
        fill="black"
      />
    </svg>
  );
};
export const FileWhiteIcon = () => {
  return (
    <svg
      width="27"
      height="27"
      viewBox="0 0 27 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.625 10.125V3.9375L20.8125 10.125M6.75 2.25C5.50125 2.25 4.5 3.25125 4.5 4.5V22.5C4.5 23.0967 4.73705 23.669 5.15901 24.091C5.58097 24.5129 6.15326 24.75 6.75 24.75H20.25C20.8467 24.75 21.419 24.5129 21.841 24.091C22.2629 23.669 22.5 23.0967 22.5 22.5V9L15.75 2.25H6.75Z"
        fill="white"
      />
    </svg>
  );
};
export const EmailIcon = () => {
  return (
    <svg
      width="27"
      height="27"
      viewBox="0 0 27 27"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21.75 3.75H5.25C4.65326 3.75 4.08097 3.98705 3.65901 4.40901C3.23705 4.83097 3 5.40326 3 6V21C3 21.5967 3.23705 22.169 3.65901 22.591C4.08097 23.0129 4.65326 23.25 5.25 23.25H21.75C22.3467 23.25 22.919 23.0129 23.341 22.591C23.7629 22.169 24 21.5967 24 21V6C24 5.40326 23.7629 4.83097 23.341 4.40901C22.919 3.98705 22.3467 3.75 21.75 3.75ZM21 18.75H6V17.25H21V18.75ZM21 15H6V13.5H21V15ZM21 11.25H6V9.75H21V11.25Z"
        fill="black"
      />
    </svg>
  );
};
